import { useState, useEffect } from 'react'
import { toast } from 'react-toastify';
import { downloadStudentHandbookSummary, getStudentSubjectsById, getStudentWorkSummaryById } from '@app/services/student';
import moment from 'moment';
import { Button } from '@app/components';
import { useSelector } from 'react-redux';
import Select from "react-select";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StudentDetailsCard from '../components/StudentDetailsCard';

const WorkSummary = () => {

    const canAccess = useSelector((state: any) => state.auth.permissions['student-work']);
    const navigate = useNavigate();
    const [t] = useTranslation();

    useEffect(() => {
        if (canAccess.summary == false) {
            toast.error(t('auth.noAccess'));
            navigate('/');
        }
    }, []);

    const { id } = useSelector((state: any) => state?.auth?.selectedStudent);
    const studentWorkStore = useSelector((state: any) => state.studentWork);

    const [isLoading, setLoading] = useState(false);
    const [isHandbookLoading, setHanbooksLoading] = useState(false);
    const [weeklyWork, setWeeklyWork] = useState([]);
    const [studentWork, setStudentWork] = useState([]);
    const [numOfWeeks, setNumOfWeeks] = useState(0);
    const [weeks, setWeeks] = useState([]);
    const [activeWeek, setActiveWeek] = useState(1);
    const [globalWeekCounter, setGlobalWeekCounter] = useState(0);
    const [streamPerWeek, setStreamPerWeek] = useState(0);
    const [search, setSearch] = useState({ subject_id: studentWorkStore?.subject?.id ?? 0 });
    const [subjects, setSubjects] = useState([]);
    const [stream1, setStream1] = useState('');
    const [stream2, setStream2] = useState('');
    const [stream3, setStream3] = useState('');
    const [stream4, setStream4] = useState('');
    const [selectedWeeks, setSelectedWeeks] = useState([]);
    const [weekError, setWeekError] = useState('');

    useEffect(() => {
        getStudentAllActiveSubjects(id);
        getStudentWorkSummary(id, search.subject_id);
    }, []);

    const getStudentAllActiveSubjects = async (studentId: any) => {
        try {
            setLoading(true);
            const resp = await getStudentSubjectsById(studentId);
            if (resp.success === true) {
                setLoading(false);
                setSubjects([{ id: 0, name: 'Mix and Match' }].concat(resp?.collections));
            } else {
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal server error');
        }
    };

    const getStudentWorkSummary = async (student_id: any, subject_id: any) => {
        try {
            setLoading(true);
            const resp = await getStudentWorkSummaryById({ student_id, subject_id });
            if (resp.success === true) {
                setLoading(false);
                const summaryData = resp?.collections?.summary;
                setStudentWork(summaryData);
                setStreamPerWeek(resp?.collections?.modules_per_week);
                setGlobalWeekCounter(resp?.collections?.global_week_counter ?? 0);
                setNumOfWeeks(summaryData?.length);
                if (summaryData?.length > 0) {
                    const weekIndex = Math.min(activeWeek - 1, summaryData.length - 1);
                    setWeeklyWork(summaryData[weekIndex >= 0 ? weekIndex : 0]);
                    if (resp?.collections?.modules_per_week >= 1) setStream1(resp?.collections?.module1 ?? '');
                    if (resp?.collections?.modules_per_week >= 2) setStream2(resp?.collections?.module2 ?? '');
                    if (resp?.collections?.modules_per_week >= 3) setStream3(resp?.collections?.module3 ?? '');
                    if (resp?.collections?.modules_per_week >= 4) setStream4(resp?.collections?.module4 ?? '');
                } else {
                    setWeeklyWork([]);
                }
                const baseCounter = resp?.collections?.global_week_counter ?? 0;
                const weeksArr = [];
                for (let i = 0; i < summaryData?.length; i++) {
                    const startDate = summaryData[i]?.[0]?.date ? moment(summaryData[i][0].date).format('DD/MM/YYYY') : '';
                    weeksArr.push({ id: i, name: `Week ${baseCounter + i + 1}${startDate ? ' - ' + startDate : ''}` });
                }
                setWeeks(weeksArr);
            } else {
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal server error');
        }
    };

    const customStylesSelect = {
        dropdownIndicator: () => ({ display: 'none' }),
    };

    const getPaginationContent = () => {
        const content = [];
        const maxVisible = 5;
        let startWeek = Math.max(1, activeWeek - Math.floor(maxVisible / 2));
        let endWeek = Math.min(numOfWeeks, startWeek + maxVisible - 1);
        if (endWeek - startWeek < maxVisible - 1) startWeek = Math.max(1, endWeek - maxVisible + 1);

        if (activeWeek > 1) content.push(<li className="page-item" key="prev"><a className="page-link" href="#" onClick={(e) => changeWeek(e, activeWeek - 1)}>«</a></li>);
        if (startWeek > 1) {
            content.push(<li className="page-item" key={1}><a className="page-link" href="#" onClick={(e) => changeWeek(e, 1)}>Week {globalWeekCounter + 1}</a></li>);
            if (startWeek > 2) content.push(<li className="page-item disabled" key="dots1"><span className="page-link">...</span></li>);
        }
        for (let i = startWeek; i <= endWeek; i++) {
            content.push(<li className="page-item" key={i}><a className="page-link" style={{ backgroundColor: activeWeek == i ? '#dee2e6' : '#fff' }} href="#" onClick={(e) => changeWeek(e, i)}>Week {globalWeekCounter + i}</a></li>);
        }
        if (endWeek < numOfWeeks) {
            if (endWeek < numOfWeeks - 1) content.push(<li className="page-item disabled" key="dots2"><span className="page-link">...</span></li>);
            content.push(<li className="page-item" key={numOfWeeks}><a className="page-link" href="#" onClick={(e) => changeWeek(e, numOfWeeks)}>Week {globalWeekCounter + numOfWeeks}</a></li>);
        }
        if (activeWeek < numOfWeeks) content.push(<li className="page-item" key="next"><a className="page-link" href="#" onClick={(e) => changeWeek(e, activeWeek + 1)}>»</a></li>);
        return content;
    };

    const changeWeek = (e: any, week: any) => {
        e.preventDefault();
        setActiveWeek(week);
        setWeeklyWork(studentWork[week - 1]);
    };

    const handleSubjectChange = async (subject_id: any) => {
        setSearch({ ...search, subject_id });
        getStudentWorkSummary(id, subject_id);
        setSelectedWeeks([]);
    };

    const onSelectWeeks = (options: readonly typeof Option[]) => {
        setSelectedWeeks([].slice.call(options).map((option: any) => option.id));
        if ([].slice.call(options).length > 0) setWeekError('');
    };

    const handlePrintHandbooks = async () => {
        try {
            if (selectedWeeks.length > 0) {
                setHanbooksLoading(true);
                setWeekError('');
                selectedWeeks.sort();
                const weeksData = selectedWeeks.map((weekIndex: any) => {
                    const weekDates = studentWork[weekIndex].map((obj: any) => obj.date);
                    return {
                        week_number: globalWeekCounter + weekIndex + 1,
                        dates: weekDates
                    };
                });
                const payload = {
                    student_id: id,
                    subject_id: search.subject_id,
                    weeks: selectedWeeks.length,
                    weeks_data: weeksData
                };
                const resp = await downloadStudentHandbookSummary(payload);
                const fileURL = URL.createObjectURL(resp);
                window.open(fileURL, '_blank');
                setHanbooksLoading(false);
            } else {
                setWeekError('Select week(s) to print the handbooks');
            }
        } catch (error) {
            setHanbooksLoading(false);
            console.log(error);
        }
    };

    return (
        <div>
            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12 text-center">
                            <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Work Summary</span>
                        </div>
                        <div className="col-md-12">
                            <StudentDetailsCard />
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Work Summary {studentWork.length == 0 ? '' : `- Week ${globalWeekCounter + activeWeek}`}</h3>
                                    {canAccess?.['print-handbooks'] && (
                                        <div className="card-tools">
                                            <div className="d-flex align-items-center">
                                                <Select
                                                    isMulti
                                                    name="week"
                                                    placeholder="Select week(s) to print"
                                                    options={weeks}
                                                    getOptionValue={(option: any) => `${option['id']}`}
                                                    getOptionLabel={(option: any) => `${option['name']}`}
                                                    onChange={(e) => { onSelectWeeks(e); }}
                                                    value={weeks?.length ? weeks.filter((obj: any) => selectedWeeks.includes(obj.id)) : selectedWeeks}
                                                    styles={customStylesSelect}
                                                    isLoading={isLoading}
                                                    isClearable={true}
                                                    closeMenuOnSelect={false}
                                                />
                                                <Button type="button" isLoading={isHandbookLoading} onClick={handlePrintHandbooks} className="btn btn-default ml-2" disabled={studentWork.length == 0}>
                                                    <i className="fas fa-print"></i> Print Handbook(s)
                                                </Button>
                                            </div>
                                            <div className="text-danger">{weekError}</div>
                                        </div>
                                    )}
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-sm-4">
                                            <label>You are viewing work for the subject</label>
                                            <select
                                                className="form-control"
                                                name="subject_id"
                                                onChange={(e) => handleSubjectChange(e.target.value)}
                                                value={search.subject_id}
                                            >
                                                {subjects.length > 0 && subjects.map((obj: any) => (
                                                    <option key={obj.id} value={obj.id}>{obj.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    {isLoading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <table className="table table-bordered">
                                            {weeklyWork.length > 0 && (
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 10 }}>#</th>
                                                        <th>Date</th>
                                                        {streamPerWeek >= 1 && <th>{stream1}</th>}
                                                        {streamPerWeek >= 2 && <th>{stream2}</th>}
                                                        {streamPerWeek >= 3 && <th>{stream3}</th>}
                                                        {streamPerWeek >= 4 && <th>{stream4}</th>}
                                                    </tr>
                                                </thead>
                                            )}
                                            <tbody>
                                                {weeklyWork.length > 0 && weeklyWork.map((obj: any, index) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}.</td>
                                                        <td>{moment(obj.date).format('DD/MM/YYYY, dddd')}</td>
                                                        {streamPerWeek >= 1 && <td>{obj?.modules !== null ? Array.prototype.map.call(obj?.modules.filter((mod: any) => mod.module == 'module1'), (m: any) => m?.name + '-' + m?.worksheet_number).join(', ') : '---'}</td>}
                                                        {streamPerWeek >= 2 && <td>{obj?.modules !== null ? Array.prototype.map.call(obj?.modules.filter((mod: any) => mod.module == 'module2'), (m: any) => m?.name + '-' + m?.worksheet_number).join(', ') : '---'}</td>}
                                                        {streamPerWeek >= 3 && <td>{obj?.modules !== null ? Array.prototype.map.call(obj?.modules.filter((mod: any) => mod.module == 'module3'), (m: any) => m?.name + '-' + m?.worksheet_number).join(', ') : '---'}</td>}
                                                        {streamPerWeek >= 4 && <td>{obj?.modules !== null ? Array.prototype.map.call(obj?.modules.filter((mod: any) => mod.module == 'module4'), (m: any) => m?.name + '-' + m?.worksheet_number).join(', ') : '---'}</td>}
                                                    </tr>
                                                ))}
                                                {weeklyWork.length == 0 && (
                                                    <tr>
                                                        <td className="text-center" colSpan={6}>There are no work summary to display</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="card-footer clearfix">
                                    <ul className="pagination pagination-sm m-0 float-right">
                                        {getPaginationContent()}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default WorkSummary;
